import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import styles from './instrumentV2.module.scss';
import {
  currentInstrumentKey,
  financialInstrumentConfig,
} from '../modules/financialInstrumentConfig';

/**
 * The instrument create/edit shell.
 *
 * Replaces the two stacked nav bars (STEP 1/STEP 2 pills + a scrolling tab
 * row) with a single rail that carries phase grouping, per-screen completion
 * and a field counter — so an operator filling ~35 fields across six screens
 * can always see what is left.
 */

// Screens that make up each phase, and the fields that decide whether a
// screen counts as done. Keys are read from the draft slice, so completion
// reflects what has actually been entered rather than what has been visited.
const STEP_ONE = [
  {
    path: '',
    label: 'General details',
    slice: 'instrument',
    required: ['name', 'description', 'issuer', 'listingType', 'currency'],
  },
  {
    path: '/trading',
    label: 'Trading settings',
    slice: 'instrument',
    required: [
      'minTradeableUnits',
      'maxTradeableUnits',
      'orderExpirationLimit',
    ],
  },
  {
    // Disclosures are authored on this step too, and the label is the only signpost —
    // "Key facts" alone sent people hunting through every other step for them.
    path: '/overview',
    label: 'Key facts & disclosures',
    slice: 'instrument',
    // Both are listed as required so the step reads incomplete until an instrument has
    // disclosures. This drives the progress indicator, not submission, so it nudges
    // without blocking an instrument that legitimately has none yet.
    required: ['keyFacts', 'disclosures'],
  },
  {
    path: '/documents',
    label: 'Documents',
    slice: 'instrument',
    required: ['documents'],
  },
];

const STEP_TWO = {
  bond: [
    {
      path: '/step-two',
      label: 'Bond details',
      slice: 'field',
      required: ['ticker', 'type', 'couponRate', 'maturityDate', 'tenor'],
    },
    {
      path: '/step-two/broker',
      label: 'Offering parties',
      slice: 'instrument',
      required: ['offering'],
    },
  ],
  ipo: [
    {
      path: '/step-two',
      label: 'IPO details',
      slice: 'field',
      required: [
        'ticker',
        'offerPrice',
        'totalSharesOnOffer',
        'subscriptionStartDate',
        'subscriptionEndDate',
      ],
    },
    {
      path: '/step-two/profile',
      label: 'Offering',
      slice: 'instrument',
      required: ['offering'],
    },
  ],
  'public-equity': [
    {
      path: '/step-two',
      label: 'Equity details',
      slice: 'field',
      required: ['ticker', 'listedAt'],
    },
  ],
  'mutual-funds': [
    {
      path: '/step-two',
      label: 'General information',
      slice: 'field',
      required: ['ticker', 'type', 'currentNetAssetValue'],
    },
    {
      path: '/step-two/funds',
      label: 'Fund details',
      slice: 'field',
      required: ['managedBy', 'investmentStrategy'],
    },
    {
      path: '/step-two/investments',
      label: 'Investments',
      slice: 'field',
      required: ['projectedAnnualReturn', 'investmentHorizon'],
    },
  ],
};

const FIELD_SLICE = {
  bond: 'bonds',
  'mutual-funds': 'mutualFunds',
  ipo: 'ipo',
  'public-equity': 'equity',
};

const filled = value => {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

export const InstrumentShell = ({type}) => {
  const {assetId, lang, id} = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const instrumentKey = currentInstrumentKey(location.pathname);
  const {title, overviewSelector} = financialInstrumentConfig[instrumentKey];

  // Step 1 writes to the "instrument" draft; step 2 writes to the field draft.
  const instrumentDraft = useSelector(overviewSelector) || {};
  // Step-2 draft slice, one per instrument type. IPO and public equity fell
  // through to the fund slice here, so their rail ticks never lit up.
  const fieldDraft =
    useSelector(state => state?.assetClass?.[FIELD_SLICE[instrumentKey]]) || {};

  const preRoute = id
    ? `/dashboard/asset-management/update/${type}/${assetId}/${id}/${lang}`
    : `/dashboard/asset-management/create/${type}/${assetId}`;

  const stepTwo = STEP_TWO[instrumentKey] || [];
  const allSteps = [...STEP_ONE, ...stepTwo];

  const draftFor = slice => (slice === 'field' ? fieldDraft : instrumentDraft);

  const doneCount = step =>
    step.required.filter(key => filled(draftFor(step.slice)[key])).length;

  const isDone = step => doneCount(step) === step.required.length;

  const totalRequired = allSteps.reduce((n, s) => n + s.required.length, 0);
  const totalDone = allSteps.reduce((n, s) => n + doneCount(s), 0);
  const percent = totalRequired
    ? Math.round((totalDone / totalRequired) * 100)
    : 0;

  // Longest matching path wins, so `/step-two/broker` beats `/step-two`.
  const activePath =
    allSteps
      .map(s => s.path)
      .filter(
        p =>
          location.pathname === `${preRoute}${p}` ||
          (p && location.pathname.startsWith(`${preRoute}${p}`)),
      )
      .sort((a, b) => b.length - a.length)[0] ?? '';

  const renderStep = (step, index) => {
    const current = step.path === activePath;
    const done = isDone(step) && !current;
    return (
      <li key={step.path || 'root'}>
        <button
          type="button"
          onClick={() => navigate(`${preRoute}${step.path}`)}
          aria-current={current || undefined}
          className={[
            styles.step,
            current ? styles.stepCurrent : '',
            done ? styles.stepDone : '',
          ]
            .filter(Boolean)
            .join(' ')}>
          <span className={styles.tick}>{done ? '✓' : index + 1}</span>
          <span className={styles.stepLabel}>{step.label}</span>
        </button>
      </li>
    );
  };

  return (
    <div className={styles.shell}>
      <aside className={styles.rail}>
        <div className={styles.railHead}>
          <button
            type="button"
            className={styles.back}
            onClick={() =>
              navigate(
                id
                  ? `/dashboard/asset-management/class/${assetId}/instruments/${id}/languages`
                  : `/dashboard/asset-management/class/${assetId}/instruments`,
              )
            }>
            ← Instruments
          </button>
          <h1 className={styles.railTitle}>
            {instrumentDraft?.name ||
              `${id ? 'Update' : 'New'} ${title.toLowerCase()}`}
          </h1>
          <div className={styles.railSub}>
            {assetId}
            {instrumentDraft?.ticker ? ` · ${instrumentDraft.ticker}` : ''}
          </div>
        </div>

        <div className={styles.phase}>Step 1 — Instrument</div>
        <ul className={styles.steps}>{STEP_ONE.map(renderStep)}</ul>

        <div className={styles.phase}>Step 2 — {title}</div>
        <ul className={styles.steps}>
          {stepTwo.map((s, i) => renderStep(s, STEP_ONE.length + i))}
        </ul>

        {/* Completion is derived from the create draft, which is empty when
            editing an existing instrument — the meter would always read 0.
            Only meaningful during creation. */}
        {!id && (
          <div className={styles.railFoot}>
            <div className={styles.meter}>
              <span style={{width: `${percent}%`}} />
            </div>
            <div className={styles.meterLabel}>
              <b>{totalDone}</b> of {totalRequired} required fields saved
            </div>
          </div>
        )}
      </aside>

      <main className={styles.body}>
        <Outlet />
      </main>
    </div>
  );
};

export default InstrumentShell;
